// src/services/api.js

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

/** 
 * ==============================================================
 * PLACES
 * ==============================================================
 */

/**
 * savePlace
 */
export const savePlace = async (placeDetails) => {
  try {
    const response = await fetch(`${API_BASE}/places`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        placeId: placeDetails.placeId,
        name: placeDetails.name,
        address: placeDetails.address,
        latitude: placeDetails.latitude,
        longitude: placeDetails.longitude,
        zipCode: placeDetails.zipCode,
        city: placeDetails.city,
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save place: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[savePlace] Error:', error.message);
    throw error;
  }
};

/**
 * fetchPairings
 *  - get pairings for a given place ID, optionally filtered by metro
 */
export const fetchPairings = async (placeInternalId, metro) => {
  try {
    let url = `${API_BASE}/pairings/${placeInternalId}`;
    if (metro && metro.toLowerCase() !== 'all') {
      url += `?metro=${encodeURIComponent(metro)}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch pairings: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[fetchPairings] Error:', error.message);
    throw error;
  }
};

/** 
 * ==============================================================
 * PAIRINGS - Token-Based Auth
 * ==============================================================
 */

/**
 * createPairing(token, place1InternalId, place2InternalId)
 */
export const createPairing = async (token, place1InternalId, place2InternalId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/pairings/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        place1_internal_id: place1InternalId,
        place2_internal_id: place2InternalId,
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create pairing: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[createPairing] Error:', error.message);
    throw error;
  }
};

/**
 * likePairing(token, pairingId)
 */
export const likePairing = async (token, pairingId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/pairings/${pairingId}/like`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[likePairing] Error:', error.message);
    throw error;
  }
};

/**
 * dislikePairing(token, pairingId)
 */
export const dislikePairing = async (token, pairingId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/pairings/${pairingId}/dislike`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[dislikePairing] Error:', error.message);
    throw error;
  }
};

/**
 * getUserVotes(token)
 */
export const getUserVotes = async (token) => {
  if (!token) return [];
  try {
    const response = await fetch(`${API_BASE}/pairings/votes`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      console.error('[getUserVotes] Bad response:', response.statusText);
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error('[getUserVotes] Error:', error.message);
    return [];
  }
};

/**
 * voteOnPairing(token, pairingId, voteType, weight)
 */
export const voteOnPairing = async (token, pairingId, voteType, weight) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/pairings/${pairingId}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ voteType, weight }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[voteOnPairing] Error:', error.message);
    throw error;
  }
};

/**
 * voteAndReloadPairings(token, { pairingId, voteType, weight, primaryPlaceId })
 */
export const voteAndReloadPairings = async (token, {
  pairingId,
  voteType,
  weight,
  primaryPlaceId,
}) => {
  if (!token) throw new Error('No token provided');
  try {
    const url = `${API_BASE}/pairings/${pairingId}/vote-and-reload`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ voteType, weight, primaryPlaceId }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[voteAndReloadPairings] Error:', error.message);
    throw error;
  }
};

/** 
 * ==============================================================
 * FAVORITES - Token-Based
 * ==============================================================
 */

/**
 * savePlaceAsFavorite(token, placeInternalId)
 */
export const savePlaceAsFavorite = async (token, placeInternalId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/favorites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ placeInternalId }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[savePlaceAsFavorite] Error:', error.message);
    throw error;
  }
};

/**
 * getUserFavorites(token)
 */
export const getUserFavorites = async (token) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/favorites`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[getUserFavorites] Error:', error.message);
    throw error;
  }
};

/**
 * removePlaceAsFavorite(token, placeInternalId)
 */
export const removePlaceAsFavorite = async (token, placeInternalId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/favorites`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ placeInternalId }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[removePlaceAsFavorite] Error:', error.message);
    throw error;
  }
};

/**
 * getUserFavoritesWithData(token)
 */
export const getUserFavoritesWithData = async (token) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/favorites/with-place-data`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[getUserFavoritesWithData] Error:', error.message);
    throw error;
  }
};

/** 
 * ==============================================================
 * NEW HELPER FUNCTIONS
 * ==============================================================
 */

/**
 * fetchPlaceBySlug(slug)
 */
export const fetchPlaceBySlug = async (slug) => {
  const url = `${API_BASE}/places/slug/${encodeURIComponent(slug)}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch place slug: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * fetchDistinctMetros
 */
export const fetchDistinctMetros = async () => {
  const url = `${API_BASE}/places/metros`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch metros: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * translateFavorites
 */
export const translateFavorites = async (token, userId, metro) => {
  const url = `${API_BASE}/pairings/translate?userId=${userId}&metro=${encodeURIComponent(metro)}`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Error in translation: ${response.status} ${response.statusText}`);
  }
  return response.json();
};


/**
 * updateUserPreferences(token, wantsUpdates)
 */
export const updateUserPreferences = async (token, wantsUpdates) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/users/update-preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ wantsUpdates }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[updateUserPreferences] Error:', error.message);
    throw error;
  }
};

/**
 * fetchUserStatsById(token, userId)
 */
export const fetchUserStatsById = async (token, userId) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/users/stats/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[fetchUserStatsById] Error:', error.message);
    throw error;
  }
};

// Add to src/services/api.js

/**
 * fetchTotalStats(token)
 */
export const fetchTotalStats = async (token) => {
  if (!token) throw new Error('No token provided');
  try {
    const response = await fetch(`${API_BASE}/users/stats/total`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return await response.json();
  } catch (error) {
    console.error('[fetchTotalStats] Error:', error.message);
    throw error;
  }
};

// Get today's daily prompt (public)
export const fetchDailyPrompt = async () => {
  const response = await fetch(`${API_BASE}/daily-prompt/today`);
  if (!response.ok) throw new Error('Failed to fetch today\'s prompt');
  return response.json();
};

// Get user's status & streak for today
export const fetchDailyPromptStatus = async (token) => {
  const response = await fetch(`${API_BASE}/daily-prompt/today-status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch prompt status');
  return response.json();
};

// Mark today's prompt as complete
export const completeDailyPrompt = async (token, promptId, pairingId) => {
  const response = await fetch(`${API_BASE}/daily-prompt/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ promptId, pairingId })
  });
  if (!response.ok) throw new Error('Failed to record completion');
  return response.json();
};
